import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import DoctorCard from '@/components/cards/DoctorCard';
import { DoctorSchema, BreadcrumbSchema } from '@/components/seo/SchemaMarkup';
import { X } from 'lucide-react';

const OurTeam = () => {
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  
  const doctors = [
    {
      name: 'Dr. Arthur Mund',
      title: 'D.D.S., Founder',
      bio: "Dr. Arthur J. Mund brings extensive training and over 40 years of cosmetic and implant dentistry experience to the North Texas area. Graduating from the University of Texas Dental School at Houston in 1982, Dr. Mund has become a leader in his field. He is board certified by the American Board of Oral Implantology and is recognized for his expertise in implant procedures, making him a sought-after dentist for complex cases referred by other professionals. When he's not in the office, Dr. Mund enjoys spending time with his family, driving his classic cars and giving back to the community through various charitable initiatives.",
      image: '/images/team/dr-mund.jpg',
      specialties: ['Dental Implants', 'Oral Implantology', 'Cosmetic Dentistry']
    },
    {
      name: 'Dr. Olena Razo',
      title: 'D.D.S.',
      bio: 'Dr. Olena Razo graduated from the University of Texas Dental School at Houston. Born in the Ukraine, she moved to Fort Worth when she was 8 years old and has called Fort Worth home ever since. Dr. Razo is passionate about patient education and preventive care. She believes in empowering her patients with knowledge about their oral health, ensuring they make informed decisions about their treatments and overall well-being.',
      image: '/images/team/dr-razo.jpg',
      specialties: ['Invisalign', 'Cosmetic Dentistry', 'Preventive Care', 'First Fit Veneers']
    },
    {
      name: 'Dr. Omar Hiwiadi',
      title: 'D.M.D.',
      bio: "Dr. Omar Hiwiadi was born and raised in Fort Worth. He specializes in cosmetic dentistry and advanced implantology, providing All-on-X procedures, FirstFit® cosmetic guided veneers, digital same-day implant tooth replacement, and 3-D printed digital dentistry.",
      fullBio: `<p>Hello, I'm Dr. Omar Hiwiadi! I was born and raised right here in Fort Worth, and my approach to dentistry is built on a simple philosophy: treating people like family and truly listening to their needs. I believe in working hand-in-hand with each person to create a treatment plan that fits their unique needs and comfort level.</p>

<p>I specialize in cosmetic dentistry and advanced implantology. Through extensive training with top specialists across the country, I focus on providing <strong>All-on-X "teeth in a day"</strong> procedures, <strong>FirstFit® cosmetic guided veneers</strong>, <strong>digital same-day implant tooth replacement</strong>, and <strong>3-D printed digital dentistry</strong>. My approach is all about collaboration, whether we are utilizing state-of-the-art digital technology for a single tooth or completing a full smile transformation, I'll take the time to make sure you're informed and comfortable every step of the way.</p>

<p>I studied at the University of North Texas, where I earned my Bachelor of Arts in Biology. I then trained at the Midwestern University College of Dental Medicine in Chicago, IL, where I earned my dental degree. I spent a few years practicing in Chicago, which is also where I met my wife, who's a dentist, too!</p>

<p>When I'm not at the office, you can usually find me hiking, exploring new cities, or just hanging out with family and friends.</p>

<p>I'm thrilled to be serving my hometown community of Fort Worth again and look forward to helping you achieve the smile you deserve!</p>`,
      image: '/images/team/dr-omar.jpg',
      specialties: ['Cosmetic Dentistry', 'Dental Implants', 'All-on-X Implants', 'Restorative Dentistry', 'First Fit Veneers']
    },
    {
      name: 'Dr. Joshua Pham',
      title: 'D.D.S.',
      bio: "Dr. Joshua Pham has resided in the DFW area for over 35 years. He earned his Doctor of Dental Surgery from the Baylor College of Dentistry in Dallas in 2011.",
      fullBio: "Dr. Joshua Pham has resided in the DFW area for over 35 years. He earned his Doctor of Dental Surgery from the Baylor College of Dentistry in Dallas in 2011. Dr. Pham regularly attends continuing education courses to stay current with the latest techniques and advancements in dentistry, ensuring his patients receive modern, high-quality care. He is also LANAP certified (Laser-Assisted New Attachment Procedure) which is an FDA-cleared, minimally invasive laser treatment for moderate-to-severe gum disease. It promotes regeneration of bone and tissue, offering less pain, faster healing and minimal gum recession.\n\nDr. Pham takes pride in helping patients of all ages feel comfortable, heard and confident in their dental care.\n\nDr. Pham believes great dentistry starts with listening. He takes time to understand each patient's concerns and goals, working together to create treatment plans that support both oral health and overall well-being.\n\nOutside the office, Dr. Pham enjoys traveling, he also loves singing, music and the arts. As an active member of the community, he values connecting with people and giving back to the area he has proudly called home for decades.",
      image: '/images/team/dr-pham.jpg',
      specialties: ['LANAP® Laser Gum Treatment', 'General Dentistry', 'Family Dentistry', 'First Fit Veneers']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Meet Our Dentists | Fort Worth Dental Team | Dr. Mund, Dr. Razo, Dr. Hiwiadi, Dr. Joshua Pham</title>
        <meta name="description" content="Meet the expert dental team at Fort Worth Dental. Dr. Mund (40+ years implant experience), Dr. Razo (Invisalign specialist), Dr. Hiwiadi, and Dr. Joshua Pham serve Fort Worth, Aledo, Benbrook TX." />
        <link rel="canonical" href="https://fortworthdental.com/our-team" />
      </Helmet>
      
      {/* Schema.org Structured Data */}
      <BreadcrumbSchema items={[
        { name: "Home", url: "https://fortworthdental.com/" },
        { name: "Our Team", url: "https://fortworthdental.com/our-team" }
      ]} />
      {doctors.map((doctor, index) => (
        <DoctorSchema 
          key={index}
          name={doctor.name}
          title={doctor.title}
          description={doctor.bio}
          image={`https://fortworthdental.com${doctor.image}`}
          specialties={doctor.specialties}
        />
      ))}
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            Meet the Fort Worth Dental Team
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our team of expert dentists and specialists are dedicated to providing you with the highest quality dental care in a warm, welcoming environment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {doctors.map((doctor, index) => (
            <DoctorCard
              key={index}
              name={doctor.name}
              title={doctor.title}
              bio={doctor.bio}
              image={doctor.image}
              onReadMore={() => setSelectedDoctor(doctor)}
            />
          ))}
        </div>

        {/* Doctor Bio Modal */}
        {selectedDoctor && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDoctor(null)}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
              <button 
                onClick={() => setSelectedDoctor(null)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full z-10"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="p-8">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
                  <div className="w-32 h-40 rounded-lg overflow-hidden border-4 border-teal-200 flex-shrink-0">
                    <img 
                      src={selectedDoctor.image}
                      alt={selectedDoctor.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{selectedDoctor.name}</h3>
                    <p className="text-teal-600 font-semibold text-lg">{selectedDoctor.title}</p>
                    {selectedDoctor.specialties && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {selectedDoctor.specialties.map((specialty, i) => (
                          <span key={i} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="prose prose-gray max-w-none">
                  {selectedDoctor.fullBio ? (
                    selectedDoctor.fullBio.includes('<p>') ? (
                      <div className="text-gray-700 leading-relaxed [&_p]:mb-4 [&_strong]:font-bold [&_strong]:text-gray-900" dangerouslySetInnerHTML={{ __html: selectedDoctor.fullBio }} />
                    ) : (
                      <div className="text-gray-700 leading-relaxed space-y-4">
                        {selectedDoctor.fullBio.split('\n\n').map((paragraph, index) => (
                          <p key={index}>{paragraph}</p>
                        ))}
                      </div>
                    )
                  ) : (
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedDoctor.bio}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Photo */}
        <div className="mt-16">
          <img
            src="/images/team/team-photo.jpg"
            alt="Fort Worth Dental team photo - Dr. Arthur Mund, Dr. Olena Razo, Dr. Omar Hiwiadi, Dr. Joshua Pham and staff at our Fort Worth TX dental office"
            className="rounded-lg shadow-2xl mx-auto max-w-5xl w-full"
          />
        </div>

        {/* Video Tour */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            Take a Virtual Tour of Our Office
          </h2>
          <div className="aspect-video rounded-lg overflow-hidden shadow-xl">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/phPxGtax2LI"
              title="Fort Worth Dental Office Tour"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        {/* Office Images */}
        <div className="mt-16 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            Our State-of-the-Art Facility
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <img
              src="/images/office/building-exterior.png"
              alt="Fort Worth Dental office exterior building at 4620 Citylake Blvd W Fort Worth TX"
              className="rounded-lg shadow-lg w-full h-64 object-cover"
            />
            <img
              src="/images/office/reception-desk.png"
              alt="Fort Worth Dental welcoming reception area with friendly staff"
              className="rounded-lg shadow-lg w-full h-64 object-cover"
            />
          </div>
        </div>

        {/* Dental Implants Video */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
            Learn About Dental Implants from Dr. Mund
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Dr. Mund, our dental implant specialist, explains the dental implant process and how it can restore your smile.
          </p>
          <div className="aspect-video rounded-lg overflow-hidden shadow-xl">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/qPyKn2Fic0w"
              title="Choosing The Right Dentist for Dental Implants - Dr. Mund"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        {/* About Section */}
        <div className="mt-16 max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            Our Commitment to Excellence
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">
            At Fort Worth Dental, it is our sincere belief that a trip to the dentist can even become an anticipated event. As you walk in the front door, our kind and professional office staff will immediately make you feel at home. Through each step of the appointment, highly-trained staff members will be seeking to meet your needs and make your appointment as comfortable as possible.
          </p>
          <p className="text-gray-700 text-lg leading-relaxed">
            We are eager to hear your questions and give you thorough answers. You may enjoy yourself so much that you don't want to leave!
          </p>
        </div>
      </div>
    </div>
  );
};

export default OurTeam;